
import {Component, Vue} from "vue-property-decorator";
import Calendar from "@/components/Calendar.vue";
import {Getter} from "vuex-class";
import BookingCreateSelection from "@/components/booking/BookingCreateSelection.vue";
import BookingCreate from "@/components/booking/BookingCreate.vue";
import BookingRemoveConfirmation from "@/components/booking/BookingRemoveConfirmation.vue";
import moment from "moment";
import MylobyLoadMore from "@/components/generic/load-more.vue";
import CompanyUsersMultiAutocomplete from "@/components/company/CompanyUsersMultiAutocomplete.vue";
import CompanyKeysMultiAutocomplete from "@/components/company/CompanyKeysMultiAutocomplete.vue";
import { mdiHistory } from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";

@Component({
    computed: {
        mdiHistory: () => mdiHistory,
    },
    components: {
        Calendar,
        CompanyUsersMultiAutocomplete,
        CompanyKeysMultiAutocomplete,
        BookingCreateSelection,
        BookingCreate,
        BookingRemoveConfirmation,
        MylobyLoadMore,
        SvgIcon,
    },
})

export default class Planning extends Vue {
    public typeToLabel: object = {
        month: "",
        week:  "",
        day:  "",
    };

    public $refs!: {
        calendar: HTMLFormElement,
        calendars: HTMLFormElement[],
    };
    public showCreateBookingSelection: boolean = false;
    public showCreateBooking: boolean = false;

    @Getter private currentUser: any;
    @Getter private envCompanyId: number|null;
    @Getter private canDoInEnvCompany: (right: string) => boolean;
    @Getter private isMobile: boolean;

    private selectedUsers: any[] = [];
    private initUser: any = null;
    private selectedKeys: any[] = [];
    private initKey: any = null;

    private focus: string = "";
    private type: string = "month";

    private newBookingKey: object = null;
    private newBookingUser: object = null;
    private newBookingStartingDate: object = null;
    private startDateResearch: string = "";
    private endDateResearch: string = "";
    private bookingEditMode: boolean = false;
    private confirmRemoval: boolean = false;
    private bookingToDelete: object = null;
    private activeEvent: object = null;
    private bookingsList: any[] = [];

    get activeList(): any[] {
        if (this.selectedUsers.length > 0) {
            return this.selectedUsers;
        } else if (this.selectedKeys.length > 0) {
            return this.selectedKeys;
        }
        return [{
            type: "all",
            value: this.$t("planning.planningAll"),
        }];
    }

    get calendarTitle(): string | void {
        if (this.startDateResearch) {
            if (this.type === "month") {
                const date = moment(this.startDateResearch);
                const month = date.format("MMM");
                const uppercasedMonth = month.charAt(0).toUpperCase() + month.slice(1);
                return `${uppercasedMonth} ${date.format("YYYY")}`;
            }
            const startDate = moment(this.startDateResearch);
            const startMonth = startDate.format("MMM");
            const endDate = moment(this.endDateResearch);
            const endMonth = endDate.format("MMM");

            const uppercasedStartMonth = startMonth.charAt(0).toUpperCase() + startMonth.slice(1);
            const uppercasedEndMonth = endMonth.charAt(0).toUpperCase() + endMonth.slice(1);
            if (uppercasedStartMonth === uppercasedEndMonth) {
                return `${uppercasedStartMonth} ${startDate.format("YYYY")}`;
            }
            return `${uppercasedStartMonth} - ${uppercasedEndMonth} ${endDate.format("YYYY")}`;
        }
    }

    get formattedCalendarEvents(): any[] {
        const events = this.bookingsList.map((booking) => {
          const startDate = moment(booking.startDate).format("YYYY-MM-DD HH:mm:ss");
          const endDate = moment(booking.endDate).format("YYYY-MM-DD HH:mm:ss");
          return {
                id: booking.id,
                name: `${(booking.User.displayName)} - ${booking.Key.name || booking.name || ""}`,
                start: startDate,
                end: endDate,
                color: booking.User.color,
                timed: true,
                user: {
                    id: booking.User.id,
                    identity: booking.User.displayName,
                },
                key: {
                    id: booking.Key.id,
                    name: booking.Key.name,
                },
                bookingName: booking.name,
                createdBy: booking.createdBy,
            };
        });
        return events;
    }

    get displaySetTodayBtn(): boolean {
        const today: string = (new Date()).toISOString();
        return this.startDateResearch > today || this.endDateResearch < today;
    }

    get displayMultiCalendars(): boolean {
        return this.type === "day" && !this.isMobile;
    }

    private beforeMount(): void {
        this.initUser = this.$route.params.user || this.currentUser;
        this.initKey = this.$route.params.key;
    }

    private mounted(): void {
        this.typeToLabel = {
            month: this.$t("planning.range.month"),
            week:  this.$t("planning.range.week"),
            day:  this.$t("planning.range.day"),
        };
    }

    private async updatedSelectedUsers(selectedUsers: any[]): Promise<void> {
      this.selectedUsers = selectedUsers;
      await this.getBookings();
    }

    private async updatedSelectedKeys(selectedKeys: any[]): Promise<void> {
      this.selectedKeys = selectedKeys;
      await this.getBookings();
    }

    private async getBookings(): Promise<void> {
        if (!this.startDateResearch || !this.endDateResearch) { return; }
        await this.$store.dispatch("bookings/read", {
            companyId: this.envCompanyId,
            startDate: this.startDateResearch,
            endDate:  this.endDateResearch,
            userIds: this.selectedUsers.map(({ id }) => id),
            keyIds: this.selectedKeys.map(({ id }) => id),
        }).then((res) => {
            this.bookingsList = res.rows;
        });
    }

    //#region Calendar features

    private setToday(): void {
        this.focus = "";
    }

    private prevIteration(): void {
        if (this.displayMultiCalendars) {
          for (const ref of this.$refs.calendars) {
            ref.prev();
          }
        } else {
          this.$refs.calendar.prev();
        }
    }

    private nextIteration(): void {
        if (this.displayMultiCalendars) {
          for (const ref of this.$refs.calendars) {
            ref.next();
          }
        } else {
          this.$refs.calendar.next();
        }
    }

    private updateFocus(newVal: string): void {
        this.focus = newVal;
    }

    private updateType(newVal: string): void {
        this.type = newVal;
    }

    private updateRange(item): void {
        const requestStartDate = new Date(item.start?.date);
        requestStartDate.setHours(0, 0 , 0);
        this.startDateResearch = moment(requestStartDate, "YYYY-MM-DD HH:mm:ss").toISOString();
        const requestEndDate = new Date(item.end?.date);
        requestEndDate.setHours(23, 59 , 59);
        this.endDateResearch = moment(requestEndDate, "YYYY-MM-DD HH:mm:ss").toISOString();
        this.getBookings();
    }

    private setNewRange(range): void {
        this.type = range;
    }

    private showEvent(event): void {
    if (event && event.key) {
      this.newBookingStartingDate = null;
      this.bookingEditMode = true;
      this.activeEvent = event;
      this.newBookingKey = event.key;
      this.newBookingUser = event.user;
      this.showCreateBooking = true;
    }
  }

    //#endregion Calendar features

    //#region Create booking

    private openAddReservationModal(item): void {
      let startingDate = null;
      if (item) {
        startingDate = new Date(
          item.date.year,
          item.date.month - 1,
          item.date.day,
        );
        let compareToDate = null;

        const isToday = (date): boolean => {
          const today = new Date();
          return date.getDate() === today.getDate()
            && date.getMonth() === today.getMonth()
            && date.getFullYear() === today.getFullYear();
        };
        if (this.type === "week" || this.type === "day") {
          startingDate.setHours(item.date.hour + 1);
          compareToDate = new Date();
        } else if (this.type === "month" && isToday(startingDate)) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          compareToDate = today;
        } else {
          compareToDate = new Date();
        }
        if (compareToDate > startingDate) {
          return;
        }
      }
      this.newBookingStartingDate = item ? item.date : null;
      this.showCreateBookingSelection = true;
    }

    private goToStep2Creation(item): void {
      this.newBookingKey = item.key;
      this.newBookingUser = {
        id: item.user.id,
        identity: item.user.displayName,
        notificationType: item.notificationType,
        startDate: item.startDate,
        endDate: item.endDate,
      };
      this.showCreateBooking = true;
    }

    private successCreation(): void {
        this.getBookings();
    }

    private closeCreationModal(): void {
        this.showCreateBooking = false;
        this.bookingEditMode = false;
        this.activeEvent = null;
    }

    //#endregion Create booking

    //#region Delete booking

    private deleteBooking(item): void {
        this.bookingToDelete = item?.booking;
        this.confirmRemoval = true;
    }

    private closeDeleteModal(): void {
        this.confirmRemoval = false;
    }

    private removeSuccess(): void {
        this.closeDeleteModal();
        this.getBookings();


    }

    //#endregion Delete booking
}
